<template>
  <transition name="filter-languages-modal" appear>
    <div
      v-if="featureEnabled && value"
      class="filter-languages-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-languages-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-languages-modal__card">
        <div class="filter-languages-modal__header">
          <span id="filter-languages-modal-title" class="filter-languages-modal__title">
            Filter languages
          </span>
          <button
            type="button"
            class="filter-languages-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-languages-modal__filters-row">
          <div class="filter-languages-modal__column">
            <span class="filter-languages-modal__column-title">Status</span>
            <div class="filter-languages-modal__options">
              <CheckboxInput
                v-for="option in STATUS_FILTER_OPTIONS"
                :key="option.value"
                :label="option.label"
                :value="selected.includes(option.value)"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>
        </div>

        <div class="filter-languages-modal__footer">
          <button type="button" class="filter-languages-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import { STATUS_FILTER_OPTIONS } from "@components/AccountLanguagesTable/accountLanguagesTableRow.js";
import { LD_FLAG_IDEA_MDP_595 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-12387 — FilterLanguagesModal (Settings > Configuration > Languages filter modal).
 *
 * Follows the Filter Modal Standard used by the sibling filter modals
 * (FilterPositionsModal / FilterJobCategoriesModal): a Status checkbox group
 * (Active / Retired) with Reset to defaults / Apply footer actions. Zero statuses
 * selected means "no status filter". The parent owns data filtering; this
 * component only re-emits intent through `apply`, `input` and `close`.
 *
 * Values are stable lowercase snake_case: `apply` emits `{ status: ["active", "retired"] }`.
 * Edits stay in a local draft that is re-synced from `initialStatuses` every time the
 * modal opens, so closing without Apply discards them.
 *
 * Shipped behind the linked idea flag (idea-MDP-595). Kill-switch semantics: renders by
 * default; set the flag to false to keep the modal closed.
 */
const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Array,
    default: () => STATUS_FILTER_OPTIONS.map((option) => option.value),
  },
});

const emit = defineEmits(["input", "close", "apply"]);

const DEFAULT_STATUSES = STATUS_FILTER_OPTIONS.map((option) => option.value);

const selected = ref([...props.initialStatuses]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  checkFeatureFlag();
});

async function checkFeatureFlag() {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_595, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
}

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});

function syncFromProps() {
  selected.value = [...(props.initialStatuses || [])];
}

function updateStatus(key, checked) {
  const others = selected.value.filter((status) => status !== key);
  selected.value = checked ? [...others, key] : others;
}

function handleReset() {
  selected.value = [...DEFAULT_STATUSES];
}

function handleApply() {
  emit("apply", {
    status: STATUS_FILTER_OPTIONS.map((option) => option.value).filter((status) =>
      selected.value.includes(status)
    ),
  });
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value && featureEnabled.value) handleClose();
}
</script>

<style scoped>
.filter-languages-modal-enter-active,
.filter-languages-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-languages-modal-enter-from,
.filter-languages-modal-leave-to {
  opacity: 0;
}

.filter-languages-modal-enter-active .filter-languages-modal__card,
.filter-languages-modal-leave-active .filter-languages-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.filter-languages-modal-enter-from .filter-languages-modal__card,
.filter-languages-modal-leave-to .filter-languages-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-languages-modal-enter-active,
  .filter-languages-modal-leave-active,
  .filter-languages-modal-enter-active .filter-languages-modal__card,
  .filter-languages-modal-leave-active .filter-languages-modal__card {
    transition: none !important;
  }
}

.filter-languages-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-languages-modal__card {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 8px -4px rgba(16, 24, 40, 0.03), 0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-languages-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-languages-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-languages-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-languages-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-languages-modal__close-btn:focus-visible,
.filter-languages-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-languages-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-languages-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-languages-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-languages-modal__options {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}

.filter-languages-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-languages-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-languages-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}
</style>
